import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Paper,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../context/AuthContext';
import { API_URL } from '../../../config/api';

const OfficialDocuments = ({ 
  selectedEmployee, 
  onStepComplete, 
  onStepIncomplete, 
  isCompleted,
  employeeProgress 
}) => {
  const { tokenManager } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  // Fetch documents for the selected employee
  useEffect(() => {
    if (selectedEmployee?.userId) {
      fetchDocuments();
    }
  }, [selectedEmployee?.userId]);

  if (!selectedEmployee) {
    return (
      <Alert severity="info">
        Please select an employee first to manage their official documents.
      </Alert>
    );
  }

  const fetchDocuments = async () => {
    if (!selectedEmployee?.userId) return;

    setLoadingDocuments(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('module', 'Process Document');
      queryParams.append('subModule', 'Employee Document');
      queryParams.append('userId', selectedEmployee.userId);

      const response = await fetch(`${API_URL}/documents?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const fileName = doc.filePath || doc.fileName;
      
      if (!fileName) {
        console.error('File not found');
        return;
      }

      // Fetch the file with authorization header
      const response = await fetch(`${API_URL}/documents/download/${fileName}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokenManager.getToken()}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Authentication failed. Please login again.');
        } else {
          console.error('Download failed');
        }
        return;
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Get the filename from Content-Disposition header
      let downloadName = doc.documentName || fileName;
      
      const contentDisposition = response.headers.get('content-disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          downloadName = filenameMatch[1];
        }
      }
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      link.style.display = 'none';
      link.id = `download-link-${Date.now()}`;
      
      document.body.appendChild(link);
      
      requestAnimationFrame(() => {
        link.click();
        
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          window.URL.revokeObjectURL(url);
        }, 500);
      });

    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleCheckboxChange = (event) => {
    if (event.target.checked) {
      onStepComplete();
    } else {
      onStepIncomplete();
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Official Documents Management
      </Typography>
      
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          This step involves processing legal documents and compliance forms for the employee.
        </Typography>

        {/* Uploaded Documents */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Employee Uploaded Documents
          </Typography>
          
          {loadingDocuments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : documents.length === 0 ? (
            <Alert severity="info">
              No documents uploaded yet by this employee.
            </Alert>
          ) : (
                          <Grid container spacing={3}>
                {documents.map((doc) => (
                  <Grid item xs={12} sm={6} md={4} key={doc.documentId || doc.id}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" component="div">
                          {doc.documentName}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(doc)}
                        >
                          Download
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
          )}
        </Box>
        
        <FormControlLabel
          control={
            <Checkbox
              checked={isCompleted}
              onChange={handleCheckboxChange}
              color="primary"
            />
          }
          label="Mark as Complete"
        />
      </Paper>
    </Box>
  );
};

export default OfficialDocuments; 